import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';

export interface TelemetryData {
  timestamp: string;
  /** AWS IoT rule server timestamp; use this for cloud-command response comparisons. */
  ingestedAt?: string | number;
  measurement: number;
  voltage: number;
  statusFlags: number;
}

export type TelemetryRange = number | { start: string; end: string };

export function useUnitTelemetry(
  unitId: string | undefined,
  range: TelemetryRange = 168
) {
  const [telemetry, setTelemetry] = useState<TelemetryData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);
  const rangeRef = useRef(range);
  rangeRef.current = range;

  // Stringify range key for hook dependency array
  const rangeKey = typeof range === 'number' ? range : `${range.start}_${range.end}`;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refetch = useCallback(async (
    rangeOverride?: TelemetryRange,
    showLoading = false,
  ): Promise<TelemetryData[]> => {
    if (!unitId) return [];
    if (showLoading && mountedRef.current) setIsLoading(true);
    try {
      const requestedRange = rangeOverride ?? rangeRef.current;
      let start: string;
      let end: string;

      if (typeof requestedRange === 'number') {
        const now = new Date();
        start = new Date(now.getTime() - requestedRange * 60 * 60 * 1000).toISOString();
        end = now.toISOString();
      } else {
        start = requestedRange.start;
        end = requestedRange.end;
      }

      // The API returns telemetry in order, default is asc
      const data = await api.get(`/api/v1/units/${unitId}/telemetry?start=${start}&end=${end}&limit=1000`);

      const mappedData: TelemetryData[] = data.map((d: any) => ({
        timestamp: d.SK, // The device timestamp is stored in SK in the backend.
        ingestedAt: d.ingestedAt,
        measurement: d.measurement,
        voltage: d.voltage,
        statusFlags: d.statusFlags || 0
      }));

      if (mountedRef.current) setTelemetry(mappedData);
      return mappedData;
    } catch (err) {
      console.error('Failed to load telemetry data', err);
      return [];
    } finally {
      if (showLoading && mountedRef.current) setIsLoading(false);
    }
  }, [unitId]);

  useEffect(() => {
    if (!unitId) {
      setTelemetry([]);
      setIsLoading(false);
      return;
    }

    void refetch(undefined, true);
  }, [unitId, rangeKey, refetch]);

  return { telemetry, isLoading, refetch };
}
